import { useContext } from 'react';
import {
  Activity,
  BarChart3,
  CalendarFold,
  ClipboardCheck,
  BookMarked,
  Bot,
  BriefcaseBusiness,
  Ellipsis,
  FlaskConical,
  HardDrive,
  KeyRound,
  Lock,
  LucideIcon,
  Network,
  Pickaxe,
  Plug,
  ScrollText,
  Settings,
  ShieldBan,
  Sparkles,
  Tags,
  Users,
  Workflow,
  Wrench,
  Zap,
} from 'lucide-react';

import { ROUTES } from '@/constants/routes';
import { AuthContext } from '@/context/auth-context';
import { featuredServers } from '@/features/ai-apps/catalog';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { useOrgStore } from '@/stores/org-store';
import { isOrgAdmin } from '@/utils/org-role';

export type NavSectionItem = {
  id: string;
  label: string;
  icon: LucideIcon;
  to: string;
};

export type NavSectionId = 'customize' | 'settings' | 'admin';

export type NavSection = {
  id: NavSectionId;
  title: string;
  /** Rail label, where "Organization Settings" would not fit. */
  shortTitle?: string;
  icon: LucideIcon;
  /** Which end of the taskbar the icon sits at. */
  placement: 'main' | 'footer';
  items: NavSectionItem[];
  /** Where the rail icon lands, and what the panel opens onto. */
  landing: string;
  matches: (pathname: string) => boolean;
};

type Candidate = NavSectionItem & { visible: boolean };

const visibleOnly = (items: Candidate[]): NavSectionItem[] =>
  items
    .filter((item) => item.visible)
    .map(({ id, label, to, icon }) => ({ id, label, to, icon }));

/**
 * The grouped destinations, each of which the design gives its own labelled
 * panel beside the taskbar. Role and flag gates live here so the rail icon and
 * the panel can never disagree about what a user can reach.
 */
export function useNavSections(): NavSection[] {
  const { user } = useContext(AuthContext);
  const { currentOrg } = useOrgStore();
  const {
    isSkillsEnabled,
    isDataSourcesEnabled,
    isContextGraphsEnabled,
    isSourcesEnabled,
    isScheduleEnabled,
    isTriggersEnabled,
    isEvalsEnabled,
    isDocsWorkflowApiEnabled,
    isOrgNodesToolsEnabled,
    isOrgMcpToolsEnabled,
    isLabelsEnabled,
    isApiKeysEnabled,
    isAgentsEnabled,
    isQualificationEnabled,
  } = useLaunchDarkly();

  const isAdminOrOwner = !!user?.isAdmin || !!user?.isOwner;
  const isInfraAdmin = !!user?.is_infra_admin;

  const customizeItems = visibleOnly([
    {
      id: 'nav-customize-data-collections',
      label: 'Data Collections',
      to: ROUTES.DATA_SOURCES.ROOT,
      icon: HardDrive,
      visible: isDataSourcesEnabled,
    },
    {
      id: 'nav-customize-a2a-agents',
      label: 'A2A Agents',
      to: ROUTES.AGENTS.ROOT,
      icon: Bot,
      // Still admin-gated: managing agents issues bearer tokens, the same blast
      // radius as API keys.
      visible: isAdminOrOwner && isAgentsEnabled,
    },
    {
      id: 'nav-customize-skills',
      label: 'Skills',
      to: ROUTES.SKILLS,
      icon: ScrollText,
      visible: isSkillsEnabled,
    },
    {
      id: 'nav-customize-evaluations',
      label: 'Evaluations',
      to: ROUTES.EVALUATION.EVALUATIONS,
      icon: Activity,
      visible: isEvalsEnabled,
    },
    {
      id: 'nav-customize-tests',
      label: 'Tests',
      to: ROUTES.EVALUATION.TESTS,
      icon: FlaskConical,
      visible: isEvalsEnabled,
    },
    {
      id: 'nav-customize-guardrails',
      label: 'Guardrails',
      to: ROUTES.GUARDRAILS.ROOT,
      icon: ShieldBan,
      // Owner/admin-only, so members aren't offered a route that bounces them.
      visible: isAdminOrOwner,
    },
    {
      id: 'nav-customize-mcp-tools',
      label: 'MCP Tools',
      to: ROUTES.SETTINGS.MY_TOOLS,
      icon: Pickaxe,
      visible: isAdminOrOwner && isOrgMcpToolsEnabled,
    },
    {
      // Capitol MCP Gateway (ENG-5304): every member reads the org's server
      // catalog; creating and managing servers is org-admin (owner or admin),
      // gated on the page and enforced by the backend.
      id: 'nav-customize-mcp-servers',
      label: 'MCP Servers',
      to: ROUTES.SETTINGS.MCP_SERVERS,
      icon: Pickaxe,
      visible: true,
    },
    {
      id: 'nav-customize-context-graphs',
      label: 'Context Graphs',
      to: ROUTES.CONTEXT_GRAPHS.ROOT,
      icon: Network,
      visible: isContextGraphsEnabled,
    },
    {
      id: 'nav-customize-triggers',
      label: 'Triggers',
      to: ROUTES.TRIGGERS.ROOT,
      icon: Zap,
      visible: isTriggersEnabled,
    },
    {
      id: 'nav-customize-connectors',
      label: 'Connectors',
      to: ROUTES.ACCOUNT.CONNECTORS,
      icon: Plug,
      visible: true,
    },
    {
      // Shown when there is something to add: an org-featured connector, or
      // gateway servers an admin can mint keys for.
      id: 'nav-customize-ai-apps',
      label: 'Add to Claude / ChatGPT',
      to: ROUTES.ACCOUNT.AI_APPS,
      icon: Sparkles,
      visible:
        featuredServers(currentOrg?.orgid).length > 0 ||
        isOrgAdmin(user, currentOrg),
    },
    {
      id: 'nav-customize-schedules',
      label: 'Schedules',
      to: ROUTES.SCHEDULES.ROOT,
      icon: CalendarFold,
      visible: isScheduleEnabled,
    },
    // Not in the mockup, but they have nowhere else to live under Geoff's rule.
    {
      id: 'nav-customize-sources',
      label: 'Sources',
      to: ROUTES.SOURCES.ROOT,
      icon: BookMarked,
      visible: isSourcesEnabled,
    },
    {
      id: 'nav-customize-workflow-api',
      label: 'Workflow API',
      to: ROUTES.DOCS.WORKFLOW_API,
      icon: Workflow,
      visible: isDocsWorkflowApiEnabled && isAdminOrOwner,
    },
  ]);

  // Mirrors the OwnerOrAdminRoute / InfraAdminRoute groups in main-routes.tsx --
  // an item added here inherits the gate instead of becoming a dead link.
  const settingsItems = visibleOnly([
    {
      id: 'nav-settings-general',
      label: 'Settings',
      to: ROUTES.SETTINGS.GENERAL,
      icon: Settings,
      visible: isAdminOrOwner,
    },
    {
      id: 'nav-settings-members',
      label: 'Members',
      to: ROUTES.SETTINGS.MEMBERS,
      icon: Users,
      visible: isAdminOrOwner,
    },
    {
      id: 'nav-settings-api-key',
      label: 'API Key',
      to: ROUTES.SETTINGS.API_KEY,
      icon: KeyRound,
      visible: isAdminOrOwner && isApiKeysEnabled,
    },
    {
      id: 'nav-settings-nodes',
      label: 'Nodes & Tools',
      to: ROUTES.SETTINGS.NODES,
      icon: Wrench,
      visible: isAdminOrOwner && isOrgNodesToolsEnabled,
    },
    {
      id: 'nav-settings-labels',
      label: 'Labels',
      to: ROUTES.SETTINGS.LABELS,
      icon: Tags,
      visible: isAdminOrOwner && isLabelsEnabled,
    },
    {
      id: 'nav-settings-encryption',
      label: 'Encryption',
      to: ROUTES.SETTINGS.ENCRYPTION,
      icon: Lock,
      visible: isInfraAdmin,
    },
    {
      id: 'nav-settings-a2a-ip-allowlist',
      label: 'A2A IP Allowlist',
      to: ROUTES.SETTINGS.A2A_IP_ALLOWLIST,
      icon: Lock,
      visible: isInfraAdmin,
    },
  ]);

  const adminItems = visibleOnly([
    {
      id: 'nav-admin-orgs',
      label: 'Organizations',
      to: ROUTES.ADMIN.ORGS,
      icon: BriefcaseBusiness,
      visible: !!user?.isAdmin,
    },
    {
      id: 'nav-admin-analytics-config',
      label: 'Analytics Config',
      to: ROUTES.ADMIN.ANALYTICS_CONFIG,
      icon: BarChart3,
      visible: !!user?.isAdmin,
    },
    {
      id: 'nav-admin-workflow-agent',
      label: 'Workflow Agent',
      to: ROUTES.ADMIN.WORKFLOW_AGENT,
      icon: Bot,
      visible: !!user?.isAdmin,
    },
    {
      id: 'nav-admin-cost',
      label: 'Cost Dashboard',
      to: ROUTES.ADMIN.COST,
      icon: BarChart3,
      visible: !!user?.isAdmin,
    },
    {
      id: 'nav-admin-uat',
      label: 'UAT Pipeline',
      to: ROUTES.ADMIN.UAT,
      icon: ClipboardCheck,
      visible: !!user?.isAdmin && isQualificationEnabled,
    },
  ]);

  const sections: NavSection[] = [
    {
      id: 'customize',
      title: 'More',
      icon: Ellipsis,
      placement: 'main',
      items: customizeItems,
      landing: customizeItems[0]?.to ?? '',
      matches: (pathname) =>
        customizeItems.some((item) => pathname.startsWith(item.to)),
    },
    {
      id: 'admin',
      title: 'Admin',
      icon: ShieldBan,
      placement: 'footer',
      items: adminItems,
      landing: adminItems[0]?.to ?? '',
      matches: (pathname) => pathname.includes(ROUTES.ADMIN.ROOT),
    },
    {
      id: 'settings',
      title: 'Organization Settings',
      shortTitle: 'Settings',
      icon: Settings,
      placement: 'footer',
      items: settingsItems,
      landing: settingsItems[0]?.to ?? '',
      matches: (pathname) => pathname.includes(ROUTES.SETTINGS.ROOT),
    },
  ];

  return sections.filter((section) => section.items.length > 0);
}

/**
 * The section a path belongs to. Resolved by the most specific item route, so a
 * destination filed under another section's prefix -- MCP Tools sits in More
 * but lives at `/settings/my-tools` -- resolves to the section that
 * actually lists it, whatever order the sections come in.
 */
export function resolveActiveSection(
  sections: NavSection[],
  pathname: string,
): NavSection | null {
  let owner: NavSection | null = null;
  let longestMatch = -1;

  for (const section of sections) {
    for (const item of section.items) {
      if (pathname.startsWith(item.to) && item.to.length > longestMatch) {
        owner = section;
        longestMatch = item.to.length;
      }
    }
  }

  return owner ?? sections.find((section) => section.matches(pathname)) ?? null;
}
